import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { ImageBackground, View } from 'react-native';
import StyleSheet from 'react-native-media-query';
import { CarouselRenderItemInfo } from 'react-native-reanimated-carousel/lib/typescript/types';
import {
  NavigationProp,
  useIsFocused,
  useNavigation,
} from '@react-navigation/native';
import dayjs from 'dayjs';

import Button from '../../../../Components/Button';
import { textCategory } from '../../../../Components/Reservation/constants';
import ResponsiveText from '../../../../Components/ResponsiveText';
import TicketIcon from '../../../../Components/SVG/Tickets';
import routeList, { ReservationNavigatorProps } from '../../../../Routes/list';
import { useTrackAdImpressionMutation } from '../../../../RTK/api/ads';
import { AdResponse } from '../../../../RTK/api/ads/types';
import ThemeColor from '../../../../Theme/colors';
import ThemeStyle, { globalSpacing } from '../../../../Theme/styles';
import breakpoints from '../../../../Theme/styles/breakpoints';

import { CARD_HEIGHT_BIG } from './constants';

let isImpressionFired = false; // outlide life cycle, to prevent firing effect twice

type BannerItemProps = CarouselRenderItemInfo<AdResponse> & {
  isVisible: boolean;
};

const BannerItem = ({ isVisible, item }: BannerItemProps) => {
  const navigation = useNavigation<NavigationProp<ReservationNavigatorProps>>();
  // const isImpressionFired = useRef(false);
  const [setAdImpression] = useTrackAdImpressionMutation();
  const isScreenFocused = useIsFocused();
  const eventDate = useMemo(() => {
    const startDayJs = dayjs(item.metadata.start_date);
    const endDayJs = dayjs(item.metadata.end_date);
    const isOneDay = endDayJs.diff(startDayJs, 'd') <= 0;
    if (isOneDay) {
      return startDayJs.format('MMMM DD, YYYY');
    } else {
      const months = [
        ...new Set([startDayJs.format('MMMM'), endDayJs.format('MMMM')]),
      ];
      const dates = [startDayJs.format('DD'), endDayJs.format('DD')];
      const range = dates.map((d, i) => `${months[i] || ''} ${d}`).join(' - ');
      return `${range}, ${startDayJs.format('YYYY')}`;
    }
  }, [item.metadata.start_date, item.metadata.end_date]);

  const LeftTicket = useCallback(
    (props) => (
      <TicketIcon
        {...props}
        fill={ThemeColor.white}
        style={styles.noMargin}
        width={30}
        height={30}
      />
    ),
    [],
  );

  const _goToEvent = () => {
    setAdImpression({ payload: { id: item.id, clicked: true } });
    navigation.navigate(routeList.RES_EVENT_DETAILS, {
      eventId: item.metadata.id,
      size: 2,
    });
  };

  useEffect(() => {
    if (isScreenFocused && isVisible && !isImpressionFired) {
      isImpressionFired = true;
      setAdImpression({ payload: { id: item.id, clicked: false } });
    }
    return () => {
      isImpressionFired = false;
    };
  }, [isScreenFocused, isVisible, item.id, setAdImpression]);

  return (
    <View style={styles.wrapper}>
      <View dataSet={{ media: ids.container }} style={styles.container}>
        <View
          dataSet={{ media: ids.textContainer }}
          style={styles.textContainer}
        >
          <View>
            <ResponsiveText
              category={textCategory.HEADING}
              status="info"
              style={[styles.letterSpacing, ThemeStyle.spacingBottomMedium]}
            >
              Pickup.ph Exclusive
            </ResponsiveText>
            <ResponsiveText
              category={textCategory.LARGE_HEADING}
              numberOfLines={1}
              style={[styles.letterSpacing, ThemeStyle.spacingBottomMedium]}
            >
              {item.metadata.name}
            </ResponsiveText>
            <ResponsiveText numberOfLines={2} style={styles.letterSpacing}>
              {item.metadata.description}
            </ResponsiveText>
          </View>
          <View>
            <View style={ThemeStyle.spacingBottom}>
              <ResponsiveText
                category={textCategory.SMALL_BODY}
                numberOfLines={1}
                status="primary"
              >
                Event Date: {eventDate}
              </ResponsiveText>
              <ResponsiveText
                numberOfLines={1}
                category={textCategory.SMALL_BODY}
                status="primary"
              >
                Location: {item.metadata.location_address}
              </ResponsiveText>
            </View>

            <View style={ThemeStyle.alignItemsStart}>
              <Button
                accessoryLeft={LeftTicket}
                status="info"
                style={styles.bannerButton}
                onPress={_goToEvent}
              >
                {() => (
                  <ResponsiveText
                    appearance="alternative"
                    category={textCategory.SMALL_BODY}
                    style={[
                      ThemeStyle.spacingLeftSmall,
                      ThemeStyle.spacingRight,
                    ]}
                  >
                    Reserve your tickets
                  </ResponsiveText>
                )}
              </Button>
            </View>
          </View>
        </View>
        <View
          dataSet={{ media: ids.imageContainer }}
          style={styles.imageContainer}
        >
          <ImageBackground
            source={{ uri: item.image_url }}
            style={styles.image}
          />
        </View>
      </View>
    </View>
  );
};

const { ids, styles } = StyleSheet.create({
  wrapper: {
    ...ThemeStyle.flex1,
    marginTop: globalSpacing / 1.8,
    paddingTop: globalSpacing,
    paddingBottom: globalSpacing,
    paddingHorizontal: 5,
  },
  container: {
    ...ThemeStyle.flex1,
    ...ThemeStyle.flexDirectionColReverse,
    ...ThemeStyle.shadowOverAll,
    borderRadius: 22,
    overflow: 'hidden',

    [`@media ${breakpoints.md}`]: ThemeStyle.flexDirectionRow,
  },
  textContainer: {
    ...ThemeStyle.flex1,
    ...ThemeStyle.pageVerticalSpacing,
    ...ThemeStyle.pageHorizontalSpacing,
    justifyContent: 'space-between',
    [`@media ${breakpoints.md}`]: {
      paddingHorizontal: globalSpacing * 2.4,
      paddingVertical: globalSpacing * 1.3,
    },
  },
  bannerButton: {
    borderRadius: 5,
    paddingVertical: 3,
    minHeight: 0,
  },
  imageContainer: {
    width: '100%',
    height: 265,
    [`@media ${breakpoints.md}`]: {
      width: '45%',
      height: CARD_HEIGHT_BIG,
    },
    [`@media ${breakpoints.lg}`]: {
      width: '49%',
    },
    [`@media ${breakpoints.xl}`]: {
      width: '52%',
    },
    [`@media ${breakpoints.xxl}`]: {
      width: '55%',
    },
    [`@media ${breakpoints.xxxl}`]: {
      width: '58%',
    },
  },
  image: { width: '100%', height: '100%' },
  letterSpacing: { letterSpacing: 0.5 },
  noMargin: { marginHorizontal: 0 },
});

export default memo(BannerItem);
